import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ChevronDown } from "lucide-react";

const lines = [["Start", "Your"], ["Aviation", "Career"], ["Today"]];
const word = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.4 + i * 0.09, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = ({ onApply, onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  let idx = 0;

  return (
    <section ref={ref} className="relative h-screen min-h-[680px] overflow-hidden bg-navy" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video autoPlay muted loop playsInline poster="/img/hero-poster.webp" className="w-full h-full object-cover" data-testid="hero-video">
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f3159] via-[#164275]/45 to-[#164275]/55" />
      <div className="absolute inset-0 grain" />

      <motion.div style={{ opacity }} className="hero-content relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center pt-28 sm:pt-24 pb-16">
        <h1 className="font-display font-light text-white tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl">
          {lines.map((ln, li) => (
            <span key={li} className="block overflow-hidden py-1">
              <span className="inline-block">
                {ln.map((w) => {
                  const i = idx++;
                  return (
                    <motion.span key={w + i} custom={i} variants={word} initial="hidden" animate="show" className="inline-block mr-[0.25em]">
                      {w === "Aviation" ? <span className="text-accent-2 italic font-normal">{w}</span> : w}
                    </motion.span>
                  );
                })}
              </span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }} className="mt-7 max-w-xl text-white/75 text-base sm:text-lg leading-relaxed">
          Train to world-class safety and airmanship standards at Rand Airport, and earn internationally recognised qualifications engineered for a professional aviation career.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.7 }} className="mt-9 flex flex-wrap gap-4">
          <button onClick={onApply} className="btn-primary px-8 py-4 text-sm sm:text-base" data-testid="hero-discovery-btn">Book a Discovery Flight</button>
          <button onClick={onExplore} className="btn-ghost px-8 py-4 text-sm sm:text-base" data-testid="hero-courses-btn">Explore Courses</button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 0.8 }} className="hero-checks mt-14 hidden md:flex flex-wrap gap-x-10 gap-y-3">
          {["Experienced Instructors", "Modern Training Fleet", "Rand Airport", "SACAA Approved"].map((s) => (
            <div key={s} className="flex items-center gap-2 text-white/70 text-sm"><span className="text-accent-2">✓</span> {s}</div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 flex flex-col items-center gap-2" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown size={18} />
      </motion.div>
    </section>
  );
};
